import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import useCreateDiagnosticStudySession from "../queries/use-create-diagnostic-study-session"
import useUpdateDiagnosticStudySession from "../queries/use-update-diagnostic-study-session"
import type { DiagnosticActivity } from "@/services/api/odin/activities/diagnostic/study-session/update"
import { usePreventNavigation } from "@/modules/activities/hooks/use-prevent-navigation"

import Header from "./header"
import Content from "./content"
import Footer from "./footer"

import DiagnosticFeedbackHeader from "./header/diagnostic-feedback-header"
import DiagnosticFeedbackContent from "./content/diagnostic-feedback"
import DiagnosticFeedbackFooter from "./footer/diagnostic-feedback-footer"

import DiagnosticQuestions from "./content/diagnostic-questions"
import DiagnosticQuestionsHeader from "./header/diagnostic-questions-header"
import { useLeftActivityAlertStore } from "../../store/use-left-activity-alert"
import useGetWeeklyObjectivesApproval from "@/modules/planner/queries/use-get-weekly-objectives-approval"
import useInvalidateStudyPlannerQueries from "../../hooks/use-invalidate-queries"
import { useProductTourV2 } from "@/modules/product-tour-v2/stores/use-product-tour"
import { useAuth } from "@/modules/auth/store"
import { PRODUCT_TOUR_V2_STEPS } from "@/modules/product-tour-v2/types/steps"

const activitiyInitial: DiagnosticActivity = {
  id: "",
  feedback: {
    approvalScore: 0,
    scorePercentage: 0,
    bullets: null,
    positive: "",
    negative: "",
  },
  review: {
    correctAnswerIndex: -1,
    review: "",
    score: 0,
  },
  next: {
    index: 0,
    question: "",
    questionType: "open_ended",
    options: null,
    answer: "",
  },
  more: true,
  total: 0,
  current: 0,
}

interface DiagnosticProps {
  id: string
  name: string
  course: string
  userActivityId: string
  userWeeklyObjectiveId: string
}

export default function Diagnostic({
  id,
  name,
  course,
  userActivityId,
  userWeeklyObjectiveId,
}: DiagnosticProps) {
  const navigator = useNavigate()
  const createSessionMutation = useCreateDiagnosticStudySession()
  const updateSessionMutation = useUpdateDiagnosticStudySession()
  const [displayReview, setDisplayReview] = useState(false)
  const [activity, setActivity] = useState<DiagnosticActivity>(activitiyInitial)
  const [activityTemp, setActivityTemp] = useState<DiagnosticActivity>(activitiyInitial)
  const { onOpen } = useLeftActivityAlertStore()
  const { data, refetch, isPending } = useGetWeeklyObjectivesApproval(userWeeklyObjectiveId)
  const handleRefetchQueries = useInvalidateStudyPlannerQueries()
  const { setCurrentStep } = useProductTourV2()
  const { user } = useAuth()

  // Use the hook to prevent navigation via browser controls
  usePreventNavigation()

  useEffect(() => {
    if (!activity.more) {
      refetch()
    }
  }, [activity.more])

  useEffect(() => {
    createStudySession()
  }, [])

  const createStudySession = async () => {
    try {
      const params = { id, body: { userActivityId } }
      const resp = await createSessionMutation.mutateAsync(params)
      setActivity({
        ...activity,
        ...resp.data.description,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnClickAnswer = async (answer: string, answerIndex: number) => {
    try {
      if (answer) {
        const resp = await updateSessionMutation.mutateAsync({
          sessionId: id,
          activityId: activity.id,
          answer,
        })

        setActivityTemp(resp.data.description)
        setDisplayReview(true)
        return
      }

      const resp = await updateSessionMutation.mutateAsync({
        sessionId: id,
        activityId: activity.id,
        answerIndex,
      })

      setActivityTemp(resp.data.description)
      setDisplayReview(true)
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnClickNextQuestion = async () => {
    try {
      setActivityTemp((temp) => {
        setActivity(temp)
        setDisplayReview(false)
        return activitiyInitial
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnContinueStudying = () => {
    handleRefetchQueries()

    if (data?.status === "approved") {
      navigator(`/comunications/${userWeeklyObjectiveId}`)
      return
    }
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  const handleOnRetryDiagnostic = () => {
    createStudySession()
  }

  const handleOnClose = () => {
    handleRefetchQueries()
    if (activity.more) {
      onOpen()
      return
    }
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  if (createSessionMutation.isPending) {
    return (
      <section className="flex size-full flex-col items-center justify-center">Cargando...</section>
    )
  }

  if (activity.more) {
    return (
      <section className="flex size-full flex-col bg-white p-2 lg:p-10">
        <Header>
          <DiagnosticQuestionsHeader
            activity={activity}
            courseName={course}
            activityName={name}
            onClose={handleOnClose}
            setActivity={setActivity}
          />
        </Header>
        <Content>
          <DiagnosticQuestions
            activity={activity}
            review={activityTemp.review}
            displayReview={displayReview}
            isLoading={updateSessionMutation.isPending}
            onClickSendAnswer={handleOnClickAnswer}
            onClickNextQuestion={handleOnClickNextQuestion}
          />
        </Content>
      </section>
    )
  }

  return (
    <section className="flex min-h-screen w-full flex-col bg-gradient-to-b from-primary-50 to-white p-2 lg:p-10">
      <Header>
        <DiagnosticFeedbackHeader onClose={handleOnClose} />
      </Header>
      <Content>
        <DiagnosticFeedbackContent feedback={activity.feedback} />
      </Content>
      <Footer>
        <DiagnosticFeedbackFooter
          activity={activity}
          onRetry={handleOnRetryDiagnostic}
          onContinueStudying={handleOnContinueStudying}
          isPending={isPending}
        />
      </Footer>
    </section>
  )
}
